import type { CSSProperties, ReactElement } from "react";

// Cave Social module platform icons (LSC-469, ADR 0214) — a renderer-only polish:
// show a recognizable platform mark alongside the existing text label in the
// published Cave. No authoring/schema change; unknown links keep the text-only
// treatment. Inline SVG (the design system uses no icon fonts, CLAUDE.md), rendered
// monochrome via `currentColor` so it inherits the Cave link colour rather than
// clashing brand colours — recognizable by shape, on-brand. The marks are the
// standard official single-path brand glyphs on a 24×24 viewBox (nominative use —
// displaying a platform's icon next to a link to that platform).

export type SocialPlatform = "linkedin" | "x" | "instagram" | "threads" | "facebook" | "youtube" | "tiktok";

// Host → platform. A configured URL is the primary signal; the label is the fallback
// (see detectSocialPlatform). Suffix-matched against the URL's hostname so
// `www.` / `m.` / regional subdomains all resolve.
const HOST_PLATFORMS: { hosts: string[]; platform: SocialPlatform }[] = [
  { platform: "linkedin", hosts: ["linkedin.com", "lnkd.in"] },
  { platform: "x", hosts: ["x.com", "twitter.com", "t.co"] },
  { platform: "instagram", hosts: ["instagram.com", "instagr.am"] },
  { platform: "threads", hosts: ["threads.com", "threads.net"] },
  { platform: "facebook", hosts: ["facebook.com", "fb.com", "fb.me"] },
  { platform: "youtube", hosts: ["youtube.com", "youtu.be"] },
  { platform: "tiktok", hosts: ["tiktok.com"] },
];

// Label keyword → platform (fallback when the URL doesn't resolve, e.g. a bare
// handle). "x" alone is too ambiguous to match on a label, so X is matched by
// "twitter" or an exact "x" label only.
function platformFromLabel(label: string): SocialPlatform | null {
  const l = label.trim().toLowerCase();
  if (!l) return null;
  if (l.includes("linkedin")) return "linkedin";
  if (l.includes("instagram") || l === "insta" || l === "ig") return "instagram";
  if (l.includes("threads")) return "threads";
  if (l.includes("facebook") || l === "fb") return "facebook";
  if (l.includes("youtube") || l === "yt") return "youtube";
  if (l.includes("tiktok")) return "tiktok";
  if (l.includes("twitter") || l === "x") return "x";
  return null;
}

/** Resolve a social link to a known platform, or null (→ keep the text-only link).
 *  URL host is authoritative; the label is the fallback. Pure + unit-tested. */
export function detectSocialPlatform(url: string, label = ""): SocialPlatform | null {
  const host = hostOf(url);
  if (host) {
    for (const { hosts, platform } of HOST_PLATFORMS) {
      if (hosts.some((h) => host === h || host.endsWith("." + h))) return platform;
    }
  }
  return platformFromLabel(label);
}

function hostOf(url: string): string | null {
  const raw = (url || "").trim();
  if (!raw) return null;
  try {
    return new URL(raw.includes("://") ? raw : `https://${raw}`).hostname.toLowerCase();
  } catch {
    return null;
  }
}

// Standard official brand glyphs (24×24 viewBox, single path, fill=currentColor).
const PATHS: Record<SocialPlatform, string> = {
  linkedin:
    "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  x: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  instagram:
    "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z",
  threads:
    "M18.263 11.097c-.03-3.486-1.92-5.586-5.111-5.586-2.13 0-3.922.963-4.863 2.499l2.062 1.438c.535-.843 1.272-1.543 2.628-1.543 1.528 0 2.318.85 2.544 2.431a15 15 0 0 0-2.236-.173c-4.125 0-6.068 1.867-6.068 4.336s1.943 3.99 4.804 3.99c3.139 0 5.013-2.115 5.781-4.735.798.361 1.348 1.204 1.348 2.47 0 3.387-3.907 5.232-7.22 5.232-4.885 0-8.077-3.207-8.077-8.424 0-6.392 4.223-10.487 9.9-10.487 3.808 0 5.69 1.671 6.97 3.914l2.108-1.475C21.44 2.078 18.331 0 13.663 0 6.227 0 1.168 5.277 1.168 12.934c0 7 4.953 11.066 10.856 11.066 4.878 0 9.809-2.846 9.809-7.716 0-2.545-1.46-4.231-3.569-5.187m-6.33 4.855c-1.077 0-2.026-.512-2.026-1.453 0-1.483 1.822-1.934 3.606-1.934.678 0 1.34.045 1.927.173-.422 1.927-1.671 3.215-3.508 3.214Z",
  facebook:
    "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  youtube:
    "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  tiktok:
    "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
};

/** The platform mark, monochrome (`currentColor`), sized to sit with the text label.
 *  `aria-hidden` (decorative) — the visible text label already names the platform. */
export function SocialIcon({ platform, size = 16, style }: { platform: SocialPlatform; size?: number; style?: CSSProperties }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden
      focusable="false"
      style={{ flex: "none", ...style }}
    >
      <path d={PATHS[platform]} />
    </svg>
  );
}
